/*
 * Fig & Bloom — hide the size options a shopper's delivery territory cannot receive.
 *
 * Covers three surfaces, all of them size pickers or size promotions:
 *   1. the product page variant picker      (snippets/variant-radios.liquid etc.)
 *   2. the quick-view modal picker          (opened by "VIEW OPTIONS" on a card)
 *   3. the cart drawer's "Upgrade to X" banner
 *
 * WHY THIS IS JAVASCRIPT
 * Liquid cannot resolve a postcode to a fulfilment location — the postcode tables
 * are JS-only and the location registry lives in the Delivery Date App's
 * APP-SCOPED metafield, which the theme cannot read. The delivery suburb is also a
 * cookie rather than a cart attribute, and product pages are CDN-cached, so there
 * is no server-side render that could know the territory. Same reasoning as
 * assets/fb-suburb-grid.js.
 *
 * FAIL OPEN, ALWAYS
 * No cookie, an unresolvable postcode, no location id, malformed data, or a rule
 * that would empty a picker — every one of those leaves the markup exactly as the
 * server rendered it. Showing a size that add-to-cart then refuses is a bad day;
 * showing a picker with nothing in it, or silently swapping a shopper onto a size
 * they did not choose, is a worse one. The real protection remains the fail-closed
 * city gate in assets/product-form.js:743-795 — this only removes the dead end
 * before the shopper walks into it.
 *
 * THE BLOCK RULE MIRRORS THAT GATE
 * A pill is hidden only when data-fb-loc-off lists THIS location id, and that
 * attribute only ever contains an explicit `false` flag. A size with no flag for
 * the location stays visible. See snippets/fb-variant-location-off.liquid for why
 * (client decision, 2 Aug 2026 — flags are still being backfilled by hand, and an
 * earlier "missing = unavailable" reading was reverted after it would have hidden
 * 152 products in Adelaide).
 *
 * NEVER EMPTY A GROUP
 * If every size in one picker is blocked, nothing is hidden. That product simply
 * is not sold into that territory, which is the collection grid's job to express
 * (assets/fb-suburb-grid.js) and the cart gate's job to enforce — not this file's.
 */
(function () {
  'use strict';

  var HIDDEN_CLASS = 'fb-loc-hidden';
  var COOKIE = 'deliveryPostcode';

  /* Same five as the rest of the codebase (delivery-suburb-popup.js
     addLocationToUrl, fb-suburb-grid.js). Only a fallback: window.fbLocationId is
     authoritative and covers a new city with no code change. */
  var STATIC_LOCATION_IDS = {
    Brisbane: '70541672681',
    Melbourne: '70507725033',
    Sydney: '70541639913',
    RestofAustralia: '74290069737',
    Adelaide: '90760315113'
  };

  function getCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  function toArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  /* window.fbResolvePostcode has TWO failure modes and only one is falsy:
       null               -> no app config on the page at all
       'Unknown Location' -> a complete config that matched nothing
     The string is truthy; treating it as a location cost a real bug in the cart
     extension on 1 Aug 2026. Both mean "we cannot place this shopper" -> fail open.

     The legacy window.findPostcodeLocation is NOT used as a fallback here on
     purpose: when its postcode arrays are missing it returns a hardcoded "Sydney"
     (delivery-suburb-popup.js:1394), and guessing a city would hide real sizes
     from someone we cannot actually place. */
  function resolveLocationId() {
    var postcode = getCookie(COOKIE);
    if (!postcode) return null;
    if (typeof window.fbResolvePostcode !== 'function') return null;

    var location = window.fbResolvePostcode(postcode);
    if (!location || location === 'Unknown Location') return null;

    var id = null;
    if (typeof window.fbLocationId === 'function') id = window.fbLocationId(location);
    id = id || STATIC_LOCATION_IDS[location] || null;
    return id ? String(id) : null;
  }

  function isBlocked(el, locationId) {
    var off = el.getAttribute('data-fb-loc-off');
    if (!off) return false;
    return off.split(',').indexOf(locationId) !== -1;
  }

  function labelFor(group, input) {
    if (!input.id) return null;
    var labels = group.getElementsByTagName('label');
    for (var i = 0; i < labels.length; i++) {
      if (labels[i].getAttribute('for') === input.id) return labels[i];
    }
    return null;
  }

  /* ---------------------------------------------------------------- pill picker */

  function filterRadioGroup(group, locationId) {
    var inputs = toArray(group.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return;

    // Reset first so a suburb change re-widens the picker instead of only ever
    // narrowing it. Only marks this file owns are cleared.
    inputs.forEach(function (input) {
      input.classList.remove(HIDDEN_CLASS);
      var label = labelFor(group, input);
      if (label) label.classList.remove(HIDDEN_CLASS);
    });
    if (!locationId) return;

    var blocked = inputs.filter(function (input) {
      return isBlocked(input, locationId);
    });
    if (!blocked.length) return;
    if (blocked.length === inputs.length) return; // never empty a group

    blocked.forEach(function (input) {
      input.classList.add(HIDDEN_CLASS);
      var label = labelFor(group, input);
      if (label) label.classList.add(HIDDEN_CLASS);
    });

    var checked = inputs.filter(function (input) { return input.checked; })[0];
    if (!checked || blocked.indexOf(checked) === -1) return;

    // The shopper's current size just disappeared. Move them to the nearest one
    // that is both deliverable and in stock, preferring in-stock but accepting a
    // sold-out size over leaving no radio checked at all — VariantRadios
    // .updateOptions() does .find(r => r.checked).value and throws on undefined
    // (assets/product-variant.js:361).
    var visible = inputs.filter(function (input) {
      return blocked.indexOf(input) === -1;
    });
    var next = visible.filter(function (input) {
      return !input.classList.contains('disabled');
    })[0] || visible[0];
    if (!next) return;

    next.checked = true;
    next.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------ dropdown picker */

  function filterSelect(select, locationId) {
    var options = toArray(select.options);
    if (!options.length) return;

    options.forEach(function (option) {
      if (option.getAttribute('data-fb-loc-hidden') === '1') {
        option.hidden = false;
        option.removeAttribute('data-fb-loc-hidden');
      }
    });
    if (!locationId) return;

    var blocked = options.filter(function (option) {
      return isBlocked(option, locationId);
    });
    if (!blocked.length) return;
    if (blocked.length === options.length) return; // never empty a group

    blocked.forEach(function (option) {
      option.hidden = true;
      option.setAttribute('data-fb-loc-hidden', '1');
    });

    if (blocked.indexOf(options[select.selectedIndex]) === -1) return;

    var next = options.filter(function (option) {
      return blocked.indexOf(option) === -1;
    })[0];
    if (!next) return;

    select.value = next.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* --------------------------------------------------- cart drawer upgrade banner */

  function filterUpgradeBanner(button, locationId) {
    var raw = button.getAttribute('data-fb-upgrade-options');
    if (!raw) return;

    var options;
    try {
      options = JSON.parse(raw);
    } catch (error) {
      return; // fail open on malformed data
    }
    if (!Array.isArray(options) || !options.length) return;

    var banner = button.closest ? button.closest('.cart-upgrade-banner') : null;
    if (banner) banner.classList.remove(HIDDEN_CLASS);
    if (!locationId) return;

    // Liquid emitted these in ascending price order (item.product.variants sorted
    // by price), so the first deliverable one is also the cheapest.
    var deliverable = options.filter(function (option) {
      return String(option.off || '').split(',').indexOf(locationId) === -1;
    });

    if (!deliverable.length) {
      // Every larger size is undeliverable here — there is no upgrade to sell.
      if (banner) banner.classList.add(HIDDEN_CLASS);
      return;
    }

    var best = deliverable[0];
    if (String(best.id) === String(button.getAttribute('data-next-variant'))) return;

    button.setAttribute('data-next-variant', String(best.id));
    button.setAttribute('data-price-difference', String(best.diff));
    var label = button.querySelector('button');
    if (label) label.textContent = best.label;
  }

  /* ------------------------------------------------------------------- the runner */

  var observers = [];

  function apply() {
    var locationId = resolveLocationId();

    toArray(document.querySelectorAll(
      'variant-radios fieldset, quick-variant-radios fieldset'
    )).forEach(function (group) {
      filterRadioGroup(group, locationId);
    });

    toArray(document.querySelectorAll(
      'variant-selects select, quick-variant-selects select'
    )).forEach(function (select) {
      filterSelect(select, locationId);
    });

    toArray(document.querySelectorAll('cart-upgrade-button')).forEach(function (button) {
      filterUpgradeBanner(button, locationId);
    });

    // Discard the mutations we just made so the observers below do not treat our
    // own edits as new content and re-enter.
    observers.forEach(function (observer) { observer.takeRecords(); });
  }

  var queued = false;
  function scheduleApply() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      apply();
    });
  }

  /* The quick-view modal and the cart drawer are both injected after load — the
     modal via innerHTML on #quickViewWrapper (quick_view_modal.js:56), the drawer
     line items via the section-rendering API into #cart-notification-product. Watch
     both for new markup rather than trying to hook every call site. */
  function observe(id) {
    var target = document.getElementById(id);
    if (!target) return;
    var observer = new MutationObserver(scheduleApply);
    observer.observe(target, { childList: true, subtree: true });
    observers.push(observer);
  }

  /* window.fbResolvePostcode is defined by an inline script the app block injects,
     so a deferred script can easily beat it to the DOM. Without this poll the first
     apply() would resolve nothing, fail open, and — because nothing else fires on a
     page where the shopper never touches the suburb picker — the sizes would simply
     never be filtered. Mirrors assets/fb-suburb-grid.js:216. Giving up quietly means
     the unfiltered picker, which is the current behaviour. */
  function whenResolverReady(cb) {
    var tries = 0;
    (function poll() {
      if (typeof window.fbResolvePostcode === 'function' || tries >= 20) return cb();
      tries++;
      setTimeout(poll, 100);
    })();
  }

  function init() {
    observe('quickViewWrapper');
    observe('cart-notification-product');

    // Fired by the delivery popup whenever the shopper picks or changes a suburb
    // (delivery-suburb-popup.js:160, :1997, :2613, :3039). Bound immediately so a
    // suburb chosen while the resolver is still loading is not missed.
    window.addEventListener('deliveryInfoUpdated', scheduleApply);

    whenResolverReady(apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
